import { inject, Injectable } from '@angular/core'
import { catchError, combineLatestWith, map, Observable, of, switchMap, throwError } from 'rxjs'
import { environment } from '../../../../environments/environment'
import { HttpClient, HttpResponse } from '@angular/common/http'
import { PieceJointeS3Dto } from './dto/piece-jointe-s3.dto'
import { PieceJointeUploadDto } from './dto/piece-jointe-upload.dto'

@Injectable({ providedIn: 'root' })
export class PieceJointeHttp {
  private static readonly endpoint = environment.baseUrl + '/piecejointe'
  private readonly http = inject(HttpClient)

  deletePieceJointe(idPieceJointe: number): Observable<string> {
    return this.http.request<string>('delete', `${PieceJointeHttp.endpoint}/delete/${idPieceJointe}`, {
      responseType: 'text' as 'json'
    })
  }

  /**
   * Uploads file to the S3 bucket
   * @param file
   * @returns le lambdaId de la piece jointe uploadée
   */
  uploadToAwsLambda(file: File): Observable<string> {
    return this.getUploadUrl(file)
      .pipe(
        switchMap((jsonDataUploadResponse) =>
          this.uploadPieceJointeToS3(file, jsonDataUploadResponse)
            .pipe(combineLatestWith(of(jsonDataUploadResponse)))
        ),
        map(([uploadS3Response, jsonDataUploadResponse]: [PieceJointeS3Dto, PieceJointeUploadDto]) => {
          if (uploadS3Response.responseStatus && uploadS3Response.responseStatus.includes('204')) {
            return jsonDataUploadResponse.filename
          } else {
            const errorMsg = `[UPLOAD FAILS] Statut: ${uploadS3Response.responseStatus}`
            console.error(errorMsg)
            throw new Error(errorMsg)
          }
        }),
        catchError(err => {
          console.error('[UPLOAD ERROR]', err)
          return throwError(() => new Error('Échec de l\'upload du fichier.'))
        })
      )
  }

  private getUploadUrl(pieceJointe: File): Observable<PieceJointeUploadDto> {
    // Utilisation bucket de DEV en local
    const lambdaBaseUrl = environment.baseUrl.includes('localhost')
      ? 'https://heuresinsertion-dev.leon-grosse.fr/heuresinsertion-api-rest'
      : environment.baseUrl

    const lambdaUploadPath =
      `${lambdaBaseUrl}/media/signed-url/generate/upload?filename=` +
      pieceJointe.name +
      `&application=` +
      `${environment.awsLambdaParam}`

    const formParams = new FormData()
    return this.http.post<PieceJointeUploadDto>(lambdaUploadPath, formParams)
  }

  private uploadPieceJointeToS3(
    pieceJointe: File,
    jsonDataUploadResponse: PieceJointeUploadDto
  ): Observable<PieceJointeS3Dto> {
    const formParams = new FormData()
    formParams.append('key', jsonDataUploadResponse.url.fields.key)
    formParams.append('x-amz-algorithm', jsonDataUploadResponse.url.fields['x-amz-algorithm'])
    formParams.append('x-amz-credential', jsonDataUploadResponse.url.fields['x-amz-credential'])
    formParams.append('x-amz-date', jsonDataUploadResponse.url.fields['x-amz-date'])
    formParams.append('x-amz-security-token', jsonDataUploadResponse.url.fields['x-amz-security-token'])
    formParams.append('policy', jsonDataUploadResponse.url.fields.policy)
    formParams.append('x-amz-signature', jsonDataUploadResponse.url.fields['x-amz-signature'])
    formParams.append('file', pieceJointe)

    const lambdaUploadS3Path = jsonDataUploadResponse.url.url
    const s3Response: PieceJointeS3Dto = { responseStatus: '' }
    return this.http.post<PieceJointeS3Dto>(lambdaUploadS3Path, formParams, { observe: 'response' }).pipe(
      map((response: HttpResponse<PieceJointeS3Dto>) => {
        s3Response.responseStatus = response.status.toString()
        return s3Response
      })
    )
  }
}
